"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSelector } from "react-redux";
import { SDK } from "@/@sdk";
import type {
  OrganizationOnboardingStatusResponse,
  UserOnboardingStatusResponse,
} from "@/@sdk/user-management/user-management.controller-models";
import type { WorkspaceWizardStepId } from "./workspace/express-workspace-onboarding-wizard";
import { selectCurrentUser } from "@/store/modules/users/users.selectors";
import { selectOrganization } from "@/store/modules/workspace/workspace.selectors";
import { useCanManageWorkspace } from "@/hooks/use-has-capability";
import { ExpressUserOnboardingWizard } from "./user/express-user-onboarding-wizard";
import { ExpressWorkspaceOnboardingWizard } from "./workspace/express-workspace-onboarding-wizard";
import {
  EXPRESS_ONBOARDING_RESUME_WORKSPACE_EVENT,
  ExpressOnboardingContext,
} from "./express-onboarding-context";

export {
  useExpressOnboarding,
  useOptionalExpressOnboarding,
} from "./express-onboarding-context";

type UserWizardStepId = "welcome" | "profile" | "done";

function parseUserResumeStep(stepId?: string): UserWizardStepId {
  if (stepId === "profile" || stepId === "done") return stepId;
  return "welcome";
}

function parseWorkspaceResumeStep(
  stepId?: string
): WorkspaceWizardStepId {
  const allowed: WorkspaceWizardStepId[] = [
    "welcome",
    "structure",
    "workspace-basics",
    "brand-loop",
    "team",
    "integrations-stripe",
    "suppliers",
    "customer-payment-methods",
    "supplier-payment-methods",
    "done",
  ];
  if (stepId === "checkpoint" || stepId === "summary") return "done";
  if (stepId === "workspace-ledgers") return "brand-loop";
  if (stepId && allowed.includes(stepId as WorkspaceWizardStepId)) {
    return stepId as WorkspaceWizardStepId;
  }
  return "welcome";
}

export function ExpressOnboardingProvider({ children }: { children?: ReactNode }) {
  const user = useSelector(selectCurrentUser);
  const organization = useSelector(selectOrganization);
  const canManageWorkspace = useCanManageWorkspace();

  const [workspaceStatus, setWorkspaceStatus] =
    useState<OrganizationOnboardingStatusResponse | null>(null);
  const [userStatus, setUserStatus] =
    useState<UserOnboardingStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorkspaceWizardOpen, setIsWorkspaceWizardOpen] = useState(false);
  const [isUserWizardOpen, setIsUserWizardOpen] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!user?.id || !organization?.id) return;

    setIsLoading(true);
    try {
      const [nextWorkspaceStatus, nextUserStatus] = await Promise.all([
        canManageWorkspace
          ? SDK.getOrganizationOnboardingStatus()
          : Promise.resolve(null),
        SDK.getUserOnboardingStatus(),
      ]);

      setWorkspaceStatus(nextWorkspaceStatus);
      setUserStatus(nextUserStatus);
      setIsWorkspaceWizardOpen(
        Boolean(nextWorkspaceStatus?.shouldShowWorkspaceWizard)
      );

      const shouldShowUserWizard =
        !nextWorkspaceStatus?.shouldShowWorkspaceWizard &&
        Boolean(nextUserStatus?.shouldShowUserWizard);
      setIsUserWizardOpen(shouldShowUserWizard);
    } catch (error) {
      console.error("Failed to load onboarding status", error);
    } finally {
      setIsLoading(false);
    }
  }, [canManageWorkspace, organization?.id, user?.id]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const resumeWorkspaceWizard = useCallback(async () => {
    const nextStatus = await SDK.patchOrganizationOnboarding({
      workspaceWizardDismissedAt: null,
    });
    setWorkspaceStatus(nextStatus);
    setIsWorkspaceWizardOpen(true);
  }, []);

  useEffect(() => {
    const handleResumeRequest = () => {
      void resumeWorkspaceWizard();
    };

    window.addEventListener(
      EXPRESS_ONBOARDING_RESUME_WORKSPACE_EVENT,
      handleResumeRequest
    );
    return () => {
      window.removeEventListener(
        EXPRESS_ONBOARDING_RESUME_WORKSPACE_EVENT,
        handleResumeRequest
      );
    };
  }, [resumeWorkspaceWizard]);

  const handleWorkspaceDismiss = useCallback(() => {
    setIsWorkspaceWizardOpen(false);
    if (userStatus?.shouldShowUserWizard) {
      setIsUserWizardOpen(true);
    }
  }, [userStatus?.shouldShowUserWizard]);

  const handleWorkspaceStatusChange = useCallback(
    (status: OrganizationOnboardingStatusResponse) => {
      setWorkspaceStatus(status);
      if (!status.shouldShowWorkspaceWizard) {
        setIsWorkspaceWizardOpen(false);
      }
    },
    []
  );

  const handleUserComplete = useCallback(() => {
    setIsUserWizardOpen(false);
    setUserStatus((prev) =>
      prev
        ? {
            ...prev,
            shouldShowUserWizard: false,
            userOnboardingCompleted: true,
          }
        : prev
    );
  }, []);

  const contextValue = useMemo(
    () => ({
      workspaceStatus,
      shouldShowContinueBanner: Boolean(
        workspaceStatus?.shouldShowContinueBanner
      ),
      canResumeWorkspaceWizard: Boolean(
        workspaceStatus && !workspaceStatus.workspaceWizardCompleted
      ),
      resumeWorkspaceWizard,
    }),
    [resumeWorkspaceWizard, workspaceStatus]
  );

  if (!user?.id || !organization?.id || isLoading) {
    return (
      <ExpressOnboardingContext.Provider value={contextValue}>
        {children}
      </ExpressOnboardingContext.Provider>
    );
  }

  return (
    <ExpressOnboardingContext.Provider value={contextValue}>
      {children}
      {isWorkspaceWizardOpen && workspaceStatus ? (
        <ExpressWorkspaceOnboardingWizard
          open={isWorkspaceWizardOpen}
          status={workspaceStatus}
          initialStepId={parseWorkspaceResumeStep(
            workspaceStatus.workspaceWizardLastStepId
          )}
          onDismiss={handleWorkspaceDismiss}
          onStatusChange={handleWorkspaceStatusChange}
        />
      ) : null}
      {!isWorkspaceWizardOpen &&
      isUserWizardOpen &&
      userStatus &&
      user ? (
        <ExpressUserOnboardingWizard
          open={isUserWizardOpen}
          user={user}
          status={userStatus}
          initialStepId={parseUserResumeStep(userStatus.userOnboardingLastStepId)}
          onComplete={handleUserComplete}
        />
      ) : null}
    </ExpressOnboardingContext.Provider>
  );
}
